import React, { useState, useEffect } from 'react';
import { 
    Mail, Users, Send, Download, RefreshCw, Loader2, 
    TrendingUp, Bell, Search, AlertCircle, CheckCircle,
    FileDown, Eye, Calendar, Clock, Power, History
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from '../components/ui/dialog';
import { apiRequest } from '../lib/utils';
import { toast } from 'sonner';

export default function MarketingAdminPage() {
    const [stats, setStats] = useState(null);
    const [subscribers, setSubscribers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sendingReminders, setSendingReminders] = useState(false);
    const [sendingCampaign, setSendingCampaign] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Campaign form
    const [campaignSubject, setCampaignSubject] = useState('');
    const [campaignContent, setCampaignContent] = useState('');
    const [targetAudience, setTargetAudience] = useState('all');
    const [showCampaignDialog, setShowCampaignDialog] = useState(false);
    const [previewHtml, setPreviewHtml] = useState('');

    // Scheduler config
    const [schedulerConfig, setSchedulerConfig] = useState(null);
    const [schedulerLogs, setSchedulerLogs] = useState([]);
    const [savingConfig, setSavingConfig] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [statsData, subsData, configData, logsData] = await Promise.all([
                apiRequest('/marketing/admin/email-stats'),
                apiRequest('/marketing/admin/subscribers'),
                apiRequest('/marketing/admin/scheduler/config').catch(() => null),
                apiRequest('/marketing/admin/scheduler/logs').catch(() => ({ logs: [] }))
            ]);
            setStats(statsData);
            setSubscribers(subsData.subscribers || []);
            setSchedulerConfig(configData);
            setSchedulerLogs(logsData.logs || []);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleSendReminders = async () => {
        setSendingReminders(true);
        try {
            const result = await apiRequest('/marketing/admin/send-analysis-reminders', {
                method: 'POST'
            });
            toast.success(result.message);
            loadData();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSendingReminders(false);
        }
    };

    const handleExportCSV = async () => {
        try {
            const data = await apiRequest('/marketing/admin/subscribers/export?format=csv');
            
            const blob = new Blob([data.csv_content], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `subscribers_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            toast.success(`${data.count} emails exportés`);
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleSendCampaign = async () => {
        if (!campaignSubject || !campaignContent) {
            toast.error('Veuillez remplir tous les champs');
            return;
        }

        setSendingCampaign(true);
        try {
            const result = await apiRequest('/marketing/admin/send-campaign', {
                method: 'POST',
                body: JSON.stringify({
                    subject: campaignSubject,
                    html_content: campaignContent,
                    target_audience: targetAudience
                })
            });
            toast.success(result.message);
            setShowCampaignDialog(false);
            setCampaignSubject('');
            setCampaignContent('');
            loadData();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSendingCampaign(false);
        }
    };

    const generateEmailTemplate = (type) => {
        const templates = {
            promo: {
                subject: "🏊 -20% sur tous les produits O.C.A PRO !",
                content: `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"></head>
<body style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
    <div style="background: linear-gradient(135deg, #0077be 0%, #005a8c 100%); padding: 30px; text-align: center; border-radius: 12px 12px 0 0;">
        <h1 style="color: white; margin: 0;">O.C.A Piscine</h1>
    </div>
    <div style="background: #f8fafc; padding: 30px; border-radius: 0 0 12px 12px;">
        <h2 style="color: #0077be; text-align: center;">-20% sur tout le site !</h2>
        <p>Profitez de notre offre exceptionnelle sur tous les produits O.C.A PRO.</p>
        <p style="text-align: center;">
            <a href="https://ocapiscine.fr/shop" style="background: #0077be; color: white; padding: 14px 30px; text-decoration: none; border-radius: 25px; display: inline-block; font-weight: bold;">En profiter maintenant</a>
        </p>
        <p style="font-size: 12px; color: #999; text-align: center; margin-top: 30px;">
            <a href="https://ocapiscine.fr/unsubscribe" style="color: #999;">Se désabonner</a>
        </p>
    </div>
</body>
</html>`
            },
            conseils: {
                subject: "💡 5 conseils pour une eau cristalline cet été",
                content: `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"></head>
<body style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
    <div style="background: linear-gradient(135deg, #0077be 0%, #005a8c 100%); padding: 30px; text-align: center; border-radius: 12px 12px 0 0;">
        <h1 style="color: white; margin: 0;">O.C.A Piscine</h1>
    </div>
    <div style="background: #f8fafc; padding: 30px; border-radius: 0 0 12px 12px;">
        <h2 style="color: #0077be;">5 conseils pour une eau cristalline</h2>
        <ol style="line-height: 1.8;">
            <li><strong>Analysez votre eau chaque semaine</strong></li>
            <li><strong>Maintenez un pH entre 7.2 et 7.4</strong></li>
            <li><strong>Nettoyez votre filtre régulièrement</strong></li>
            <li><strong>Passez le robot 2 fois par semaine</strong></li>
            <li><strong>Utilisez un anti-algues préventif</strong></li>
        </ol>
        <p style="text-align: center;">
            <a href="https://ocapiscine.fr/analysis" style="background: #0077be; color: white; padding: 14px 30px; text-decoration: none; border-radius: 25px; display: inline-block; font-weight: bold;">Analyser mon eau</a>
        </p>
    </div>
</body>
</html>`
            },
            nouveaute: {
                subject: "🆕 Découvrez nos nouveaux produits O.C.A PRO",
                content: `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"></head>
<body style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
    <div style="background: linear-gradient(135deg, #0077be 0%, #005a8c 100%); padding: 30px; text-align: center; border-radius: 12px 12px 0 0;">
        <h1 style="color: white; margin: 0;">O.C.A Piscine</h1>
        <p style="color: rgba(255,255,255,0.9); margin: 10px 0 0 0;">Nouveautés</p>
    </div>
    <div style="background: #f8fafc; padding: 30px; border-radius: 0 0 12px 12px;">
        <h2 style="color: #0077be; text-align: center;">Nouveaux produits disponibles !</h2>
        <p>Découvrez notre gamme élargie de produits professionnels pour l'entretien de votre piscine.</p>
        <p style="text-align: center;">
            <a href="https://ocapiscine.fr/shop" style="background: #0077be; color: white; padding: 14px 30px; text-decoration: none; border-radius: 25px; display: inline-block; font-weight: bold;">Voir les nouveautés</a>
        </p>
    </div>
</body>
</html>`
            }
        };

        const template = templates[type];
        setCampaignSubject(template.subject);
        setCampaignContent(template.content);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const filteredSubscribers = subscribers.filter(sub =>
        sub.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (sub.name && sub.name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="marketing-admin">
            {/* Header Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-full">
                                <Mail className="h-6 w-6 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Abonnés Newsletter</p>
                                <p className="text-2xl font-bold">{stats?.newsletter?.active || 0}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-full">
                                <Users className="h-6 w-6 text-green-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Utilisateurs inscrits</p>
                                <p className="text-2xl font-bold">{stats?.users?.total || 0}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="bg-purple-100 dark:bg-purple-900/30 p-3 rounded-full">
                                <TrendingUp className="h-6 w-6 text-purple-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Emails atteignables</p>
                                <p className="text-2xl font-bold">{stats?.total_reachable || 0}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="bg-amber-100 dark:bg-amber-900/30 p-3 rounded-full">
                                <Send className="h-6 w-6 text-amber-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Campagnes envoyées</p>
                                <p className="text-2xl font-bold">{stats?.recent_campaigns?.length || 0}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Tabs defaultValue="subscribers">
                <TabsList>
                    <TabsTrigger value="subscribers">Abonnés</TabsTrigger>
                    <TabsTrigger value="campaigns">Campagnes</TabsTrigger>
                    <TabsTrigger value="reminders">Rappels Auto</TabsTrigger>
                </TabsList>

                {/* Subscribers Tab */}
                <TabsContent value="subscribers" className="space-y-4">
                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Rechercher un email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={loadData}>
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Actualiser
                            </Button>
                            <Button variant="outline" onClick={handleExportCSV}>
                                <FileDown className="h-4 w-4 mr-2" />
                                Exporter CSV
                            </Button>
                        </div>
                    </div>

                    <Card>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Nom</TableHead>
                                    <TableHead>Source</TableHead>
                                    <TableHead>Statut</TableHead>
                                    <TableHead>Date inscription</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredSubscribers.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                            Aucun abonné trouvé
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredSubscribers.map((sub) => (
                                        <TableRow key={sub.subscriber_id}>
                                            <TableCell className="font-medium">{sub.email}</TableCell>
                                            <TableCell>{sub.name || '-'}</TableCell>
                                            <TableCell>
                                                <Badge variant="outline">{sub.source}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge className={sub.status === 'active' ? 'bg-green-500' : 'bg-red-500'}>
                                                    {sub.status === 'active' ? 'Actif' : 'Désabonné'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>{formatDate(sub.created_at)}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </Card>

                    {/* Sources breakdown */}
                    {stats?.sources && Object.keys(stats.sources).length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Sources d'inscription</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-wrap gap-4">
                                    {Object.entries(stats.sources).map(([source, count]) => (
                                        <div key={source} className="flex items-center gap-2">
                                            <Badge variant="outline">{source}</Badge>
                                            <span className="font-semibold">{count}</span>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>

                {/* Campaigns Tab */}
                <TabsContent value="campaigns" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Envoyer une campagne email</CardTitle>
                            <CardDescription>
                                Créez et envoyez des emails marketing à vos abonnés
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex gap-2 flex-wrap">
                                <Button variant="outline" onClick={() => generateEmailTemplate('promo')}>
                                    Template Promo
                                </Button>
                                <Button variant="outline" onClick={() => generateEmailTemplate('conseils')}>
                                    Template Conseils
                                </Button>
                                <Button variant="outline" onClick={() => generateEmailTemplate('nouveaute')}>
                                    Template Nouveauté
                                </Button>
                            </div>

                            <div className="space-y-2">
                                <Label>Objet de l'email</Label>
                                <Input
                                    value={campaignSubject}
                                    onChange={(e) => setCampaignSubject(e.target.value)}
                                    placeholder="Objet de votre email..."
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Contenu HTML</Label>
                                <Textarea
                                    value={campaignContent}
                                    onChange={(e) => setCampaignContent(e.target.value)}
                                    placeholder="Collez votre code HTML ici..."
                                    rows={10}
                                    className="font-mono text-sm"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Audience cible</Label>
                                <Select value={targetAudience} onValueChange={setTargetAudience}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous les contacts ({stats?.total_reachable || 0})</SelectItem>
                                        <SelectItem value="newsletter_only">Newsletter uniquement ({stats?.newsletter?.active || 0})</SelectItem>
                                        <SelectItem value="active_users">Utilisateurs inscrits ({stats?.users?.total || 0})</SelectItem>
                                        <SelectItem value="recent_buyers">Acheteurs récents (30j)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="flex gap-2">
                                <Dialog>
                                    <DialogTrigger asChild>
                                        <Button variant="outline" disabled={!campaignContent}>
                                            <Eye className="h-4 w-4 mr-2" />
                                            Prévisualiser
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
                                        <DialogHeader>
                                            <DialogTitle>Prévisualisation</DialogTitle>
                                        </DialogHeader>
                                        <div 
                                            className="border rounded-lg p-4 bg-white"
                                            dangerouslySetInnerHTML={{ __html: campaignContent }}
                                        />
                                    </DialogContent>
                                </Dialog>

                                <Button 
                                    onClick={handleSendCampaign} 
                                    disabled={sendingCampaign || !campaignSubject || !campaignContent}
                                >
                                    {sendingCampaign ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            Envoi en cours...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="h-4 w-4 mr-2" />
                                            Envoyer la campagne
                                        </>
                                    )}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Recent campaigns */}
                    {stats?.recent_campaigns?.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Campagnes récentes</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Objet</TableHead>
                                            <TableHead>Audience</TableHead>
                                            <TableHead>Envoyés</TableHead>
                                            <TableHead>Date</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {stats.recent_campaigns.map((campaign) => (
                                            <TableRow key={campaign.campaign_id}>
                                                <TableCell className="font-medium">{campaign.subject}</TableCell>
                                                <TableCell>
                                                    <Badge variant="outline">{campaign.target_audience}</Badge>
                                                </TableCell>
                                                <TableCell>{campaign.recipient_count}</TableCell>
                                                <TableCell>{formatDate(campaign.sent_at)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>

                {/* Reminders Tab */}
                <TabsContent value="reminders" className="space-y-4">
                    {/* Automation Configuration */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Clock className="h-5 w-5" />
                                Automatisation des rappels
                            </CardTitle>
                            <CardDescription>
                                Configurez l'envoi automatique hebdomadaire des rappels d'analyse
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {schedulerConfig && (
                                <>
                                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <Power className={`h-5 w-5 ${schedulerConfig.enabled ? 'text-green-500' : 'text-muted-foreground'}`} />
                                            <div>
                                                <p className="font-medium">Envoi automatique</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {schedulerConfig.enabled ? 'Activé' : 'Désactivé'}
                                                </p>
                                            </div>
                                        </div>
                                        <Switch
                                            checked={schedulerConfig.enabled}
                                            onCheckedChange={async (checked) => {
                                                setSchedulerConfig({...schedulerConfig, enabled: checked});
                                                setSavingConfig(true);
                                                try {
                                                    await apiRequest(`/marketing/admin/scheduler/config?enabled=${checked}&day=${schedulerConfig.day}&hour=${schedulerConfig.hour}`, {
                                                        method: 'POST'
                                                    });
                                                    toast.success(checked ? 'Automatisation activée' : 'Automatisation désactivée');
                                                } catch (error) {
                                                    toast.error(error.message);
                                                } finally {
                                                    setSavingConfig(false);
                                                }
                                            }}
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Jour d'envoi</Label>
                                            <Select
                                                value={schedulerConfig.day}
                                                onValueChange={async (value) => {
                                                    setSchedulerConfig({...schedulerConfig, day: value});
                                                    setSavingConfig(true);
                                                    try {
                                                        await apiRequest(`/marketing/admin/scheduler/config?enabled=${schedulerConfig.enabled}&day=${value}&hour=${schedulerConfig.hour}`, {
                                                            method: 'POST'
                                                        });
                                                        toast.success('Configuration mise à jour');
                                                    } catch (error) {
                                                        toast.error(error.message);
                                                    } finally {
                                                        setSavingConfig(false);
                                                    }
                                                }}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="monday">Lundi</SelectItem>
                                                    <SelectItem value="tuesday">Mardi</SelectItem>
                                                    <SelectItem value="wednesday">Mercredi</SelectItem>
                                                    <SelectItem value="thursday">Jeudi</SelectItem>
                                                    <SelectItem value="friday">Vendredi</SelectItem>
                                                    <SelectItem value="saturday">Samedi</SelectItem>
                                                    <SelectItem value="sunday">Dimanche</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Heure d'envoi</Label>
                                            <Select
                                                value={schedulerConfig.hour?.toString()}
                                                onValueChange={async (value) => {
                                                    const hour = parseInt(value);
                                                    setSchedulerConfig({...schedulerConfig, hour});
                                                    setSavingConfig(true);
                                                    try {
                                                        await apiRequest(`/marketing/admin/scheduler/config?enabled=${schedulerConfig.enabled}&day=${schedulerConfig.day}&hour=${hour}`, {
                                                            method: 'POST'
                                                        });
                                                        toast.success('Configuration mise à jour');
                                                    } catch (error) {
                                                        toast.error(error.message);
                                                    } finally {
                                                        setSavingConfig(false);
                                                    }
                                                }}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {[...Array(24)].map((_, i) => (
                                                        <SelectItem key={i} value={i.toString()}>
                                                            {i.toString().padStart(2, '0')}:00
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>

                                    {schedulerConfig.last_run && (
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <CheckCircle className="h-4 w-4 text-green-500" />
                                            Dernier envoi : {new Date(schedulerConfig.last_run).toLocaleString('fr-FR')} 
                                            ({schedulerConfig.last_run_count} emails)
                                        </div>
                                    )}
                                </>
                            )}
                        </CardContent>
                    </Card>

                    {/* Manual Trigger */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Bell className="h-5 w-5" />
                                Envoi manuel
                            </CardTitle>
                            <CardDescription>
                                Déclenchez immédiatement l'envoi des rappels sans attendre l'automatisation
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                <div className="flex items-start gap-3">
                                    <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                                    <div>
                                        <p className="font-medium text-blue-900 dark:text-blue-100">
                                            Comment ça marche ?
                                        </p>
                                        <ul className="text-sm text-blue-800 dark:text-blue-200 mt-2 space-y-1">
                                            <li>• Analyse les utilisateurs sans analyse depuis 7+ jours</li>
                                            <li>• Respecte les préférences de notification de chaque utilisateur</li>
                                            <li>• Envoie un email personnalisé avec lien direct vers l'analyse</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <Button 
                                onClick={handleSendReminders} 
                                disabled={sendingReminders}
                                size="lg"
                                className="w-full"
                            >
                                {sendingReminders ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Envoi des rappels...
                                    </>
                                ) : (
                                    <>
                                        <Send className="h-4 w-4 mr-2" />
                                        Envoyer les rappels maintenant
                                    </>
                                )}
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Scheduler Logs */}
                    {schedulerLogs.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2 text-lg">
                                    <History className="h-5 w-5" />
                                    Historique des envois
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Type</TableHead>
                                            <TableHead>Emails</TableHead>
                                            <TableHead>Statut</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {schedulerLogs.map((log, index) => (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    {new Date(log.timestamp).toLocaleString('fr-FR')}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline">
                                                        {log.trigger === 'manual' ? 'Manuel' : 'Auto'}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>{log.emails_sent}</TableCell>
                                                <TableCell>
                                                    {log.success ? (
                                                        <Badge className="bg-green-500">Succès</Badge>
                                                    ) : (
                                                        <Badge className="bg-red-500">Erreur</Badge>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}
